import { getMarketplaceGeneralSettings, updateMarketplaceGeneralSettings } from 'APIs/marketplaces/marketplaces';
import {
  MarketplaceGeneralSettingsResponse,
  MarketplaceGeneralSettingsUpdateRequest,
} from 'APIs/marketplaces/marketplaces.general.settings.types';
import Spinner from 'Components/Spinner/Spinner';
import { Content, LpBox } from 'Constants/styles';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import GeneralSettingsForm from './GeneralSettingsForm';
import GeneralSettingsView from './GeneralSettingsView';
import { EditMerchantSettings, MerchantSettingsHeader, MessageWrapper } from './styles';

const MarketplaceGeneralSettings = () => {
  const { marketplaceId } = useParams<{ marketplaceId: string; manage: string }>();
  const apiBaseUri = useSelector((state: ReduxStateType) => state.apiBaseUri);
  const [generalSettings, setGeneralSettings] = useState<MarketplaceGeneralSettingsResponse | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [updateErrorMsg, setUpdateErrorMsg] = useState<string>('');
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

  const fetchGeneralSettings = useCallback(async () => {
    try {
      setIsFetching(true);
      const resp = await getMarketplaceGeneralSettings(apiBaseUri, marketplaceId);
      setGeneralSettings(resp);
    } catch (error) {
      setGeneralSettings(null);
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  }, [apiBaseUri, marketplaceId]);

  const updateGeneralSettings = useCallback(
    async (data: MarketplaceGeneralSettingsUpdateRequest): Promise<void> => {
      try {
        setIsUpdating(true);

        await updateMarketplaceGeneralSettings(apiBaseUri, marketplaceId, data);
        fetchGeneralSettings();
        setUpdateErrorMsg('');
        setShowUpdateModal(false);
        toast.success('Successfully updated marketplace general settings');
      } catch (error) {
        setUpdateErrorMsg(getApiErrorMessage(error));
      } finally {
        setIsUpdating(false);
      }
    },
    [apiBaseUri, fetchGeneralSettings, marketplaceId],
  );

  const handleEditButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setUpdateErrorMsg('');
    setShowUpdateModal(true);
  };

  useEffect(() => {
    if (!marketplaceId) {
      return;
    }
    fetchGeneralSettings();
  }, [fetchGeneralSettings, marketplaceId]);

  return (
    <>
      <Content>
        <LpBox>
          {isFetching && !generalSettings ? (
            <MessageWrapper>
              <Spinner color="#0016D1" height="25px" width="25px" />
            </MessageWrapper>
          ) : (
            <>
              <MerchantSettingsHeader>
                <h3 className="mb-0">General settings</h3>

                <EditMerchantSettings onClick={handleEditButtonClick}>
                  {generalSettings ? 'Edit' : 'Add'}
                </EditMerchantSettings>
              </MerchantSettingsHeader>

              {showUpdateModal && (
                <GeneralSettingsForm
                  generalSettings={generalSettings}
                  isLoading={isUpdating}
                  errorMsg={updateErrorMsg}
                  updateGeneralSettings={updateGeneralSettings}
                  setShowUpdateModal={setShowUpdateModal}
                />
              )}

              <GeneralSettingsView generalSettings={generalSettings} />
            </>
          )}
        </LpBox>
      </Content>
    </>
  );
};

export default MarketplaceGeneralSettings;
